import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useUserData } from '../lib/useUserData.js'
import { useToast } from '../context/ToastContext.jsx'
import { MONTHS, DOW, monthGrid, toKey, todayKey, prettyDate } from '../lib/dates.js'
import { DOSE_UNITS } from '../lib/peptide.js'
import Modal from '../components/Modal.jsx'
import Seo from '../components/Seo.jsx'

export default function Calendar() {
  const { events, calcs, toggleTaken, deleteEvent, addEvent } = useUserData()
  const toast = useToast()

  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth())
  const [openDay, setOpenDay] = useState(null)

  const byDay = useMemo(() => {
    const map = {}
    for (const e of events) (map[e.date] ||= []).push(e)
    for (const k in map) map[k].sort((a, b) => (a.time || '').localeCompare(b.time || ''))
    return map
  }, [events])

  const grid = useMemo(() => monthGrid(year, month), [year, month])
  const today = todayKey()

  const shift = (delta) => {
    let m = month + delta
    let y = year
    if (m < 0) { m = 11; y-- }
    if (m > 11) { m = 0; y++ }
    setMonth(m)
    setYear(y)
  }
  const goToday = () => { setMonth(now.getMonth()); setYear(now.getFullYear()) }

  const total = events.length
  const taken = events.filter((e) => e.taken).length

  return (
    <main className="page">
      <Seo title="Dosing Calendar | CalculateMyPeptides" noindex />
      <div className="container">
        <div className="between wrap" style={{ marginBottom: 18 }}>
          <div>
            <h1 className="section-title">Dosing Calendar</h1>
            <p className="section-sub mb-0">
              {total ? `${taken}/${total} doses logged` : 'Schedule and track your injections.'}
            </p>
          </div>
          {calcs.length > 0 && (
            <Link to="/saved" className="btn btn-soft btn-sm">🗓️ Schedule from a saved calc</Link>
          )}
        </div>

        <div className="card card-pad">
          <div className="cal-head">
            <div className="cal-nav">
              <button className="icon-btn" onClick={() => shift(-1)} aria-label="Previous month">‹</button>
              <div className="cal-month">{MONTHS[month]} {year}</div>
              <button className="icon-btn" onClick={() => shift(1)} aria-label="Next month">›</button>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={goToday}>Today</button>
          </div>

          <div className="cal-grid" style={{ marginBottom: 6 }}>
            {DOW.map((d) => <div key={d} className="cal-dow">{d}</div>)}
          </div>
          <div className="cal-grid">
            {grid.map((date) => {
              const key = toKey(date)
              const inMonth = date.getMonth() === month
              const dayEvents = byDay[key] || []
              return (
                <button
                  key={key}
                  className={`cal-cell ${inMonth ? '' : 'dim'} ${key === today ? 'today' : ''}`}
                  onClick={() => setOpenDay(key)}
                >
                  <span className="cal-date">{date.getDate()}</span>
                  {dayEvents.slice(0, 3).map((e) => (
                    <span key={e.id} className={`cal-event ${e.taken ? 'taken' : ''}`}>
                      {e.units != null ? `${e.units}u ` : ''}{e.name}
                    </span>
                  ))}
                  {dayEvents.length > 3 && <span className="cal-more">+{dayEvents.length - 3} more</span>}
                </button>
              )
            })}
          </div>
        </div>

        {total === 0 && (
          <div className="card empty" style={{ marginTop: 20 }}>
            <div className="emoji">🗓️</div>
            <h3 style={{ marginBottom: 6 }}>No doses scheduled</h3>
            <p style={{ marginBottom: 18 }}>
              Save a calculation, then schedule it here — or click any day to add a dose manually.
            </p>
            <Link to="/saved" className="btn btn-primary">Go to my calculations</Link>
          </div>
        )}
      </div>

      {openDay && (
        <DayModal
          dayKey={openDay}
          events={byDay[openDay] || []}
          calcs={calcs}
          onClose={() => setOpenDay(null)}
          onToggle={toggleTaken}
          onDelete={(e) => {
            deleteEvent(e.id)
            toast('Dose removed', 'default', 5000, {
              label: 'Undo',
              onClick: () => addEvent({
                date: e.date, time: e.time, calcId: e.calcId, name: e.name,
                dose: e.dose, doseUnit: e.doseUnit, units: e.units, taken: e.taken,
              }),
            })
          }}
          onAdd={(payload) => { addEvent(payload); toast('Dose added', 'green') }}
        />
      )}
    </main>
  )
}

function DayModal({ dayKey, events, calcs, onClose, onToggle, onDelete, onAdd }) {
  const [adding, setAdding] = useState(false)
  const [calcId, setCalcId] = useState(calcs[0]?.id || '')
  const [time, setTime] = useState('08:00')
  const [customName, setCustomName] = useState('')

  const unitLabel = (id) => DOSE_UNITS.find((u) => u.id === id)?.label || 'mcg'
  const fmtTime = (t) => {
    if (!t) return ''
    const [h, m] = t.split(':').map(Number)
    const ap = h < 12 ? 'AM' : 'PM'
    return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${ap}`
  }

  const submitAdd = () => {
    const c = calcs.find((x) => x.id === calcId)
    if (c) {
      onAdd({
        date: dayKey, time, calcId: c.id, name: c.name,
        dose: c.dose, doseUnit: c.doseUnit, units: c.snapshot?.units ?? null,
      })
    } else {
      onAdd({ date: dayKey, time, name: customName.trim() || 'Dose', units: null })
    }
    setAdding(false)
  }

  return (
    <Modal title={prettyDate(dayKey)} onClose={onClose}>
      <div className="modal-body">
        {events.length === 0 && !adding && (
          <p className="muted center" style={{ padding: '10px 0' }}>No doses on this day yet.</p>
        )}

        {events.map((e) => (
          <div className={`event-row ${e.taken ? 'is-taken' : ''}`} key={e.id}>
            <div className="event-info">
              <div className="t">
                {e.name}
                {e.units != null && <span className="event-units">{e.units}u</span>}
              </div>
              <div className="s">
                {e.units != null ? `Draw ${e.units} units` : 'Dose'}
                {e.dose ? ` · ${e.dose} ${unitLabel(e.doseUnit)}` : ''}
                {e.time ? ` · ${fmtTime(e.time)}` : ''}
              </div>
            </div>
            <button className={`take-btn ${e.taken ? 'on' : ''}`} onClick={() => onToggle(e.id)}>
              {e.taken ? '✓ Taken' : 'Mark taken'}
            </button>
            <button className="event-del" onClick={() => onDelete(e)} aria-label="Remove dose">✕</button>
          </div>
        ))}

        {adding && (
          <div style={{ marginTop: events.length ? 16 : 0 }}>
            <div className="field">
              <label className="label">Dose</label>
              {calcs.length > 0 ? (
                <select className="select" value={calcId} onChange={(e) => setCalcId(e.target.value)}>
                  {calcs.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} — {c.dose} {unitLabel(c.doseUnit)}</option>
                  ))}
                  <option value="">Custom…</option>
                </select>
              ) : (
                <input className="input" placeholder="Dose name" value={customName}
                  onChange={(e) => setCustomName(e.target.value)} />
              )}
            </div>
            {calcs.length > 0 && calcId === '' && (
              <div className="field">
                <label className="label">Name</label>
                <input className="input" placeholder="e.g. BPC-157" value={customName}
                  onChange={(e) => setCustomName(e.target.value)} />
              </div>
            )}
            <div className="field mb-0">
              <label className="label">Time</label>
              <input className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          </div>
        )}
      </div>
      <div className="modal-foot">
        {adding ? (
          <>
            <button className="btn btn-ghost" onClick={() => setAdding(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={submitAdd}>Add dose</button>
          </>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={() => setAdding(true)}>+ Add a dose</button>
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </>
        )}
      </div>
    </Modal>
  )
}
